import {
  createOrganization,
  getOrganizationList,
  joinRequest,
} from '@api/organization.js';
import { editUserDetails, fetchUserDetails } from '@api/user.js';
import Tabs from '@components/Common/Tabs.js';
import toast from '@components/Common/Toast.js';
import Modal from '@components/Modals/Modal.js';
import OrganizationState from '@states/Organization.js';
import UserState from '@states/User.js';
import { UIButton, UIDiv, UIInput, UIText, UITextArea } from '@ui';

export default class JoinCreateOrgModal extends Modal {
  constructor() {
    super(i18next.t('modal.joinCreateOrg.title'), {
      showCancel: true,
      showConfirm: false,
      showCloseIcon: true,
      modalContentClass: 'max-w-2xl',
      contentContainerClass: 'min-h-[400px]',
    });

    this.isLoading = false;

    this.referenceId = '';
    this.requestNotes = '';

    this.organizationName = '';
    this.logo = null;

    this.resolve = null;
  }

  createModal() {
    const modal = super.createModal();

    const joinTabContent = this.createJoinContent();
    const createTabContent = this.createCreateContent();

    const tabs = new Tabs(
      [
        {
          display: null,
          title: i18next.t('modal.joinCreateOrg.join_tab'),
          content: joinTabContent,
          bgColor: null,
          callback: () => {
            setTimeout(() => {
              if (this.orgIdInput) {
                this.orgIdInput.dom.focus();
              }
            }, 100);
          },
        },
        {
          display: null,
          title: i18next.t('modal.joinCreateOrg.create_tab'),
          content: createTabContent,
          bgColor: null,
          callback: () => {
            setTimeout(() => {
              if (this.orgNameInput) {
                this.orgNameInput.dom.focus();
              }
            }, 100);
          },
        },
      ],
      'p-6',
    );

    this.contentContainer.add(tabs);

    setTimeout(() => {
      tabs.setActiveTab(0);
    }, 100);

    return modal;
  }

  createJoinContent() {
    const container = new UIDiv().addClass('space-y-6');

    const description = new UIText(
      i18next.t('modal.joinCreateOrg.join_description'),
    ).addClass('text-gray-600 text-center');

    const idExplanation = new UIText(
      i18next.t('modal.joinCreateOrg.join_explanation'),
    ).addClass('text-sm text-gray-500 text-center');

    const idContainer = new UIDiv().addClass('space-y-2');
    const idLabel = new UIText(
      i18next.t('modal.joinCreateOrg.org_id_label'),
    ).addClass('block text-sm font-medium text-gray-700');
    this.orgIdInput = new UIInput()
      .setPlaceholder(i18next.t('modal.joinCreateOrg.org_id_placeholder'))
      .addClass(
        'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors',
      )
      .setValue(this.referenceId)
      .onInput(e => {
        this.referenceId = e.target.value;
        this.validateJoinForm();
      });

    idContainer.add(idLabel, this.orgIdInput);

    const notesContainer = new UIDiv().addClass('space-y-2');
    const notesLabel = new UIText(
      i18next.t('modal.joinCreateOrg.message_label'),
    ).addClass('block text-sm font-medium text-gray-700');
    this.notesTextArea = new UITextArea()
      .setPlaceholder(i18next.t('modal.joinCreateOrg.message_placeholder'))
      .addClass(
        'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-y min-h-[80px] max-h-[150px]',
      )
      .setRows(4)
      .setValue(this.requestNotes)
      .onInput(e => {
        this.requestNotes = e.target.value;
      });

    notesContainer.add(notesLabel, this.notesTextArea);

    this.joinButton = new UIButton(
      i18next.t('modal.joinCreateOrg.send_request'),
    )
      .addClass(
        'w-full py-3 px-4 bg-[var(--o-hive-primary-100)] text-white rounded-md enabled:hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 enabled:hover:shadow-md',
      )
      .setDisabled(true)
      .onClick(() => this.handleJoinOrg());

    container.add(
      description,
      idExplanation,
      idContainer,
      notesContainer,
      this.joinButton,
    );

    return container;
  }

  createCreateContent() {
    const container = new UIDiv().addClass('space-y-6');

    const description = new UIText(
      i18next.t('modal.joinCreateOrg.create_description'),
    ).addClass('text-gray-600 text-center');

    const logoContainer = new UIDiv().addClass('space-y-2');
    const logoLabel = new UIText(
      i18next.t('modal.joinCreateOrg.logo_label'),
    ).addClass('block text-sm font-medium text-gray-700');

    this.logoInput = new UIInput()
      .setType('file')
      .setAttribute('accept', 'image/*')
      .addClass(
        'w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-all',
      )
      .onChange(e => this.handleLogoUpload(e));

    const logoInfo = new UIDiv().addClass(
      'text-xs text-gray-500 mt-1 space-y-1',
    );
    logoInfo.add(
      new UIText(i18next.t('modal.joinCreateOrg.logo_formats')).addClass(
        'block',
      ),
      new UIText(i18next.t('modal.joinCreateOrg.logo_size')).addClass('block'),
    );

    logoContainer.add(logoLabel, this.logoInput, logoInfo);

    const nameContainer = new UIDiv().addClass('space-y-2');
    const nameLabel = new UIText(
      i18next.t('modal.joinCreateOrg.org_name_label'),
    ).addClass('block text-sm font-medium text-gray-700');
    this.orgNameInput = new UIInput()
      .setPlaceholder(i18next.t('modal.joinCreateOrg.org_name_placeholder'))
      .setMaxLength(50)
      .addClass(
        'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors',
      )
      .setValue(this.organizationName)
      .onInput(e => {
        this.organizationName = e.target.value;
        this.validateCreateForm();
      });

    nameContainer.add(nameLabel, this.orgNameInput);

    this.createButton = new UIButton(
      i18next.t('modal.joinCreateOrg.create_org'),
    )
      .addClass(
        'w-full py-3 px-4 bg-[var(--o-hive-primary-100)] text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-md',
      )
      .setDisabled(true)
      .onClick(() => this.handleCreateOrg());

    container.add(description, logoContainer, nameContainer, this.createButton);

    return container;
  }

  validateJoinForm() {
    const isValid = this.referenceId.trim().length > 0;
    if (this.joinButton) {
      this.joinButton.setDisabled(!isValid || this.isLoading);
    }
  }

  validateCreateForm() {
    const isValid = this.organizationName.trim().length > 0;
    if (this.createButton) {
      this.createButton.setDisabled(!isValid || this.isLoading);
    }
  }

  handleLogoUpload(event) {
    const files = event.target.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.includes('image')) {
        this.logo = file;
        toast.success(i18next.t('modal.joinCreateOrg.logo_uploaded'));
      } else {
        toast.error(i18next.t('modal.joinCreateOrg.invalid_image'));
        this.logoInput.setValue('');
        this.logo = null;
      }
    }
  }

  async handleJoinOrg() {
    if (this.isLoading) return;

    if (!this.referenceId.trim()) {
      toast.error(i18next.t('modal.joinCreateOrg.enter_org_id'));
      return;
    }

    this.isLoading = true;
    this.joinButton.setInnerText(
      i18next.t('modal.joinCreateOrg.sending_request'),
    );
    this.joinButton.setDisabled(true);

    try {
      const response = await joinRequest({
        referenceID: this.referenceId.trim(),
        notes: this.requestNotes,
      });

      if (!response.error) {
        try {
          const orgazations = await getOrganizationList();
          OrganizationState.setOrganizations(orgazations.response);
        } catch (error) {
          console.error('Failed to refresh user details:', error);
        }

        toast.success(i18next.t('modal.joinCreateOrg.request_sent'));
        this.close(true);
      } else {
        toast.error(i18next.t('modal.joinCreateOrg.request_failed'));
      }
    } catch (error) {
      const errorMessage = error?.response;
      console.error('Error requesting to join organization:', error);
      toast.error(
        errorMessage || i18next.t('modal.joinCreateOrg.request_error'),
      );
    } finally {
      this.isLoading = false;
      this.joinButton.setInnerText(
        i18next.t('modal.joinCreateOrg.send_request'),
      );
      this.validateJoinForm();
    }
  }

  async handleCreateOrg() {
    if (this.isLoading) return;

    const orgName = this.organizationName.trim();
    if (!orgName) {
      toast.error(i18next.t('modal.joinCreateOrg.enter_org_name'));
      return;
    }

    this.isLoading = true;
    this.createButton.setInnerText(i18next.t('modal.joinCreateOrg.creating'));
    this.createButton.setDisabled(true);

    try {
      const formData = new FormData();
      formData.append('organization', orgName);
      if (this.logo) formData.append('logo', this.logo);

      const response = await createOrganization(formData);

      if (response.error) {
        this.handleCreateOrgError(response.error);
        return;
      }

      const orgData = response.response.createOrganization;
      const organizationId = orgData.organizationId;

      try {
        const user = await fetchUserDetails();
        const userDetails = {
          ...user.body.user,
          orgData: user.body.orgData,
        };
        UserState.setUser(userDetails);
      } catch (err) {
        console.error('Failed to refresh user details:', err);
      }

      if (response.statusCode === 200) {
        await editUserDetails({ selectedOrg: organizationId });
        UserState.setUser({
          ...UserState.user,
          selectedOrg: organizationId,
        });

        const newOrg = {
          ...orgData,
          organizationId,
          name: orgName,
          role: 'admin',
          uid: UserState.user.uid,
          createdAt: new Date().toISOString(),
          logoKey: orgData.logoKey || null,
        };

        OrganizationState.setOrganizations([
          ...OrganizationState.organizations,
          newOrg,
        ]);
      }

      toast.success(i18next.t('modal.joinCreateOrg.org_created'));
      this.close(true);
    } catch (error) {
      console.error('Error creating organization:', error);
      toast.error(i18next.t('modal.joinCreateOrg.create_error'));
    } finally {
      this.isLoading = false;
      this.createButton.setInnerText(
        i18next.t('modal.joinCreateOrg.create_org'),
      );
      this.validateCreateForm();
    }
  }

  handleCreateOrgError(error) {
    const message = error?.response?.data?.response?.toLowerCase() || '';
    if (message.includes('already exist')) {
      toast.error(i18next.t('modal.joinCreateOrg.org_exists'));
    } else if (message.includes('already has')) {
      toast.error(i18next.t('modal.joinCreateOrg.user_has_org'));
    } else {
      toast.error(i18next.t('modal.joinCreateOrg.create_failed'));
    }
  }

  show(elemId) {
    return new Promise(resolve => {
      this.resolve = resolve;
      super.show(elemId);
    });
  }

  close(result) {
    if (this.resolve) {
      this.resolve(result);
    }
    super.close(result);
  }
}
